import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { restaurantGroupAPI, tenantAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Loader2 } from 'lucide-react';

// ── Currency config ───────────────────────────────────────────────────────────

export const CURRENCY_CONFIG = [
  { code: 'USD', label: 'US Dollar',          symbol: '$'  },
  { code: 'CAD', label: 'Canadian Dollar',    symbol: 'C$' },
  { code: 'EUR', label: 'Euro',               symbol: '€'  },
  { code: 'GBP', label: 'British Pound',      symbol: '£'  },
  { code: 'PHP', label: 'Philippine Peso',    symbol: '₱'  },
  { code: 'AUD', label: 'Australian Dollar',  symbol: 'A$' },
];

const CURRENCY_SYMBOLS = Object.fromEntries(CURRENCY_CONFIG.map(c => [c.code, c.symbol]));

/**
 * Format a numeric value as a currency string.
 * Can be used standalone (outside the React tree) by passing currency explicitly.
 */
export function formatMoney(value, currency = 'USD') {
  const symbol = CURRENCY_SYMBOLS[currency] || '$';
  const num = typeof value === 'number' ? value : Number(value) || 0;
  return `${symbol}${num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// ── Context ───────────────────────────────────────────────────────────────────

const CurrencyContext = createContext({
  reportCurrency: 'USD',
  formatMoney: (v) => formatMoney(v, 'USD'),
  saveCurrency: async () => {},
  currencies: CURRENCY_CONFIG,
  setShowSetupModal: () => {},
});

export function useCurrency() {
  return useContext(CurrencyContext);
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function CurrencyProvider({ children }) {
  const { user } = useAuth();
  const [reportCurrency, setReportCurrency] = useState('USD');
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchCurrency = useCallback(async () => {
    if (!user) return;
    try {
      const res = await tenantAPI.getSettings();
      const currency = res.data?.report_currency;
      if (currency) {
        setReportCurrency(currency);
        setShowSetupModal(false);
      } else if (user.role === 'owner') {
        // No currency set yet — show one-time setup modal for owner
        setShowSetupModal(true);
      }
    } catch {
      // Fail silently — default to USD
    }
  }, [user]);

  useEffect(() => {
    fetchCurrency();
  }, [fetchCurrency]);

  const saveCurrency = useCallback(async (code) => {
    setSaving(true);
    try {
      await restaurantGroupAPI.updateSettings({ report_currency: code });
      setReportCurrency(code);
      setShowSetupModal(false);
    } catch {
      // Error handling done via toast in caller if needed
    } finally {
      setSaving(false);
    }
  }, []);

  const fmtMoney = useCallback(
    (value) => formatMoney(value, reportCurrency),
    [reportCurrency],
  );

  return (
    <CurrencyContext.Provider
      value={{
        reportCurrency,
        formatMoney: fmtMoney,
        saveCurrency,
        currencies: CURRENCY_CONFIG,
        setShowSetupModal,
      }}
    >
      {children}
      {showSetupModal && user?.role === 'owner' && (
        <CurrencySetupModal onSave={saveCurrency} saving={saving} />
      )}
    </CurrencyContext.Provider>
  );
}

// ── One-time setup modal ───────────────────────────────────────────────────────

function CurrencySetupModal({ onSave, saving }) {
  const [selected, setSelected] = useState('USD');

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4"
      data-testid="currency-setup-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
          <h2
            className="text-lg font-bold text-slate-900"
            style={{ fontFamily: 'Manrope' }}
            data-testid="currency-modal-title"
          >
            Choose your reporting currency
          </h2>
          <p className="text-sm text-slate-500 mt-1.5">
            Select the currency used for payroll and workforce reports.
            You can change this later in Business Settings.
          </p>
        </div>

        {/* Select */}
        <div className="px-6 py-5">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="h-11" data-testid="currency-setup-select">
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              {CURRENCY_CONFIG.map(c => (
                <SelectItem
                  key={c.code}
                  value={c.code}
                  data-testid={`currency-option-${c.code}`}
                >
                  <span className="font-semibold text-slate-700">{c.symbol}</span>
                  <span className="ml-2 text-slate-600">{c.code} — {c.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 pb-6">
          <Button
            onClick={() => onSave(selected)}
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="currency-setup-save-btn"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            Save Currency
          </Button>
        </div>
      </div>
    </div>
  );
}
